import React, { useState } from 'react';
import { Users, GraduationCap, BookOpen, Building2, Shield, FileText, Moon, type LucideIcon } from 'lucide-react';

interface DemoAccount {
  email:    string;
  password: string;
  labelMs:  string;
  labelEn:  string;
  roleMs:   string;
  roleEn:   string;
  Icon:     LucideIcon;
  color:    string;
}

// All 7 demo accounts — one per RBAC role in the tender matrix
const ACCOUNTS: DemoAccount[] = [
  {
    email: 'admin@demo.serps.bn', password: 'Demo@2026',
    labelMs: 'Pentadbir Sekolah', labelEn: 'School Admin',
    roleMs: 'Sekolah SMAS Ali', roleEn: 'SMAS Ali School',
    Icon: Users, color: '#1D4ED8',
  },
  {
    email: 'guru@demo.serps.bn', password: 'Demo@2026',
    labelMs: 'Guru', labelEn: 'Teacher',
    roleMs: 'Kelas T4A / T5A', roleEn: 'Classes 4A / 5A',
    Icon: GraduationCap, color: '#059669',
  },
  {
    email: 'ibubapa@demo.serps.bn', password: 'Demo@2026',
    labelMs: 'Ibu Bapa / Penjaga', labelEn: 'Parent / Guardian',
    roleMs: 'Anak: T4A, T2B', roleEn: 'Children: 4A, 2B',
    Icon: BookOpen, color: '#D97706',
  },
  {
    email: 'moe@demo.serps.bn', password: 'Demo@2026',
    labelMs: 'Pegawai KPM', labelEn: 'MOE Officer',
    roleMs: 'Semua Sekolah (Baca)', roleEn: 'All Schools (Read)',
    Icon: Building2, color: '#7C3AED',
  },
  {
    email: 'dpe@demo.serps.bn', password: 'Demo@2026',
    labelMs: 'Pegawai DPE', labelEn: 'DPE Officer',
    roleMs: 'Sekolah Swasta', roleEn: 'Private Schools',
    Icon: FileText, color: '#DC2626',
  },
  {
    email: 'mora@demo.serps.bn', password: 'Demo@2026',
    labelMs: 'Pegawai MORA', labelEn: 'MORA Officer',
    roleMs: 'Sekolah Agama', roleEn: 'Religious Schools',
    Icon: Moon, color: '#0D9488',
  },
  {
    email: 'superadmin@moe.edu.bn', password: 'Demo@2026',
    labelMs: 'Pentadbir Super', labelEn: 'Super Admin',
    roleMs: 'Akses Penuh', roleEn: 'Full Access',
    Icon: Shield, color: '#374151',
  },
];

interface Props {
  onLogin:  (email: string, password: string) => Promise<void>;
  loading:  boolean;
  lang?:    string;
}

export function DemoLoginPanel({ onLogin, loading, lang = 'ms' }: Props) {
  const [activeEmail, setActiveEmail] = useState<string | null>(null);

  async function handleClick(acc: DemoAccount) {
    if (loading) return;
    setActiveEmail(acc.email);
    try {
      await onLogin(acc.email, acc.password);
    } finally {
      setActiveEmail(null);
    }
  }

  return (
    <div
      style={{
        marginTop:       '20px',
        backgroundColor: '#FFFFFF',
        border:          '1px solid #E5E7EB',
        borderRadius:    '12px',
        overflow:        'hidden',
        boxShadow:       '0 1px 3px rgba(0,0,0,0.06)',
      }}
      role="region"
      aria-label={lang === 'en' ? 'Demo quick login' : 'Log masuk demo pantas'}
    >
      {/* Header */}
      <div style={{ padding: '12px 16px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ backgroundColor: '#F7D000', color: '#1A1A1A', padding: '2px 10px', borderRadius: '999px', fontSize: '10px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          DEMO RBAC
        </span>
        <span style={{ fontSize: '12px', color: '#6B7280' }}>
          {lang === 'en' ? 'Click any role — RBAC isolates each view' : 'Klik mana-mana peranan — RBAC mengasingkan setiap paparan'}
        </span>
      </div>

      {/* 2-column role grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        {ACCOUNTS.map((acc, i) => {
          const col        = i % 2;
          const isLast     = i >= ACCOUNTS.length - (ACCOUNTS.length % 2 === 0 ? 2 : 1);
          const borderRight  = col === 0 ? '1px solid #E5E7EB' : 'none';
          const borderBottom = !isLast ? '1px solid #E5E7EB' : 'none';
          return (
            <DemoRoleButton
              key={acc.email}
              acc={acc}
              lang={lang}
              disabled={loading}
              spinning={activeEmail === acc.email}
              borderRight={borderRight}
              borderBottom={borderBottom}
              onClick={() => void handleClick(acc)}
            />
          );
        })}
      </div>

      <p style={{ margin: 0, padding: '8px 16px', fontSize: '11px', color: '#9CA3AF', textAlign: 'center', borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
        {lang === 'en'
          ? 'Demo mode · All accounts use password: Demo@2026'
          : 'Mod demo · Semua akaun menggunakan kata laluan: Demo@2026'}
      </p>
    </div>
  );
}

function DemoRoleButton({ acc, lang, disabled, spinning, borderRight, borderBottom, onClick }: {
  acc: DemoAccount; lang: string; disabled: boolean; spinning: boolean;
  borderRight: string; borderBottom: string; onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`${lang === 'en' ? acc.labelEn : acc.labelMs} (${acc.email})`}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '6px', padding: '14px 10px',
        backgroundColor: hovered ? `${acc.color}08` : '#FFFFFF',
        border: 'none', borderRight, borderBottom,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled && !spinning ? 0.5 : 1,
        transition: 'background-color 0.15s',
        textAlign: 'center', fontFamily: '"Inter", sans-serif',
      }}
    >
      <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: `${acc.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {spinning
          ? <span style={{ width: '16px', height: '16px', border: `2px solid ${acc.color}40`, borderTopColor: acc.color, borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
          : <acc.Icon size={18} color={acc.color} strokeWidth={1.8} />
        }
      </div>
      <div>
        <div style={{ fontSize: '11px', fontWeight: 700, color: '#1A1A1A', lineHeight: 1.3 }}>
          {lang === 'en' ? acc.labelEn : acc.labelMs}
        </div>
        <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '2px' }}>
          {lang === 'en' ? acc.roleEn : acc.roleMs}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </button>
  );
}
